import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, money, fileUrl } from "@/lib/api";
import { MEDIA } from "@/lib/media";
import { Reveal } from "@/components/Reveal";

const TYPE_IMG = { multiline: MEDIA.cableBalcony, panel: MEDIA.hero, picket: MEDIA.picketClose };

export default function Configurator() {
  const [cfg, setCfg] = useState(null);
  const [type, setType] = useState("panel");
  const [len, setLen] = useState(40);
  const [finish, setFinish] = useState("satin");
  const [mount, setMount] = useState("fascia");
  const [opts, setOpts] = useState(["handrail"]);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/configurator").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const est = useMemo(() => {
    if (!cfg) return null;
    const t = cfg.types.find((x) => x.id === type);
    if (!t) return null;
    const fin = cfg.finishes.find((x) => x.id === finish) || { mult: 1 };
    const mnt = cfg.mounts.find((x) => x.id === mount) || { add: 0 };
    let perFt = t.base * (fin.mult || 1) + (mnt.add || 0);
    let mult = 1;
    opts.forEach((id) => { const o = cfg.options.find((x) => x.id === id); if (o) { if (o.add_per_ft) perFt += o.add_per_ft; if (o.mult) mult *= o.mult; } });
    perFt = perFt * mult + (cfg.install_per_ft || 0);
    let total = perFt * Number(len || 0);
    total = Math.max(total, cfg.min_project || 0);
    return { perFt, total, low: total * 0.92, high: total * 1.12 };
  }, [cfg, type, finish, mount, opts, len]);

  const toggleOpt = (id) => setOpts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) { toast.error("Name and email are required"); return; }
    const t = cfg.types.find((x) => x.id === type);
    const optLabels = opts.map((id) => cfg.options.find((o) => o.id === id)?.label).filter(Boolean).join(", ");
    const desc = `Configurator estimate — ${t?.label}, ${len} linear ft. Finish: ${finish}. Mount: ${mount}. Options: ${optLabels || "none"}. Estimated range ${money(est.low)}–${money(est.high)}.`;
    setSubmitting(true);
    try {
      await api.post("/leads", { name: lead.name, email: lead.email, phone: lead.phone, project_type: t?.label, budget: `${money(est.low)}–${money(est.high)}`, description: desc });
      setDone(true);
      toast.success("Estimate request sent");
    } catch { toast.error("Something went wrong"); } finally { setSubmitting(false); }
  };

  if (!cfg) return <div className="min-h-screen flex items-center justify-center text-onyx-metal"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  const sel = "border-onyx-black bg-onyx-black text-white";
  const unsel = "border-black/15 hover:border-onyx-black";

  return (
    <div className="min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-40 pb-12">
        <span className="text-[11px] tracking-[0.3em] uppercase text-onyx-metal">Instant Estimate</span>
        <h1 className="font-display font-extrabold text-5xl md:text-8xl tracking-tightest mt-5 leading-[0.9] max-w-4xl">Configure your railing.</h1>
        <p className="text-onyx-metal text-lg mt-8 max-w-2xl leading-relaxed">Choose a system, set the length, and see a live budget range. Send it to us and we'll turn it into a formal proposal.</p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-32 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10">
          {/* type */}
          <div data-testid="config-types">
            <h3 className="text-[11px] tracking-[0.2em] uppercase text-onyx-metal mb-4">1 · Railing System</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {cfg.types.map((t) => (
                <button key={t.id} data-testid={`config-type-${t.id}`} onClick={() => setType(t.id)}
                  className={`group text-left border transition-colors overflow-hidden ${type === t.id ? "border-onyx-black" : "border-black/15 hover:border-onyx-black"}`}>
                  <div className="h-32 overflow-hidden"><img src={fileUrl(TYPE_IMG[t.id] || MEDIA.hero)} alt={t.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold">{t.label}</span>
                      {type === t.id && <Check size={16} className="text-onyx-wood" />}
                    </div>
                    <p className="text-xs text-onyx-metal mt-1">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* length + height */}
          <div>
            <h3 className="text-[11px] tracking-[0.2em] uppercase text-onyx-metal mb-4">2 · Length</h3>
            <div className="flex items-center gap-6">
              <input data-testid="config-length" type="range" min="5" max="500" value={len} onChange={(e) => setLen(e.target.value)} className="flex-1 accent-onyx-black" />
              <div className="flex items-baseline gap-2">
                <input type="number" value={len} onChange={(e) => setLen(e.target.value)} className="w-24 border-b border-black/30 py-2 text-2xl font-display font-bold outline-none focus:border-onyx-black bg-transparent" />
                <span className="text-onyx-metal text-sm">linear ft</span>
              </div>
            </div>
          </div>

          {/* finish */}
          <div>
            <h3 className="text-[11px] tracking-[0.2em] uppercase text-onyx-metal mb-4">3 · Finish</h3>
            <div className="flex flex-wrap gap-3">
              {cfg.finishes.map((fn) => (
                <button key={fn.id} data-testid={`config-finish-${fn.id}`} onClick={() => setFinish(fn.id)} className={`px-5 py-3 border text-sm transition-colors ${finish === fn.id ? sel : unsel}`}>{fn.label}</button>
              ))}
            </div>
          </div>

          {/* mount */}
          <div>
            <h3 className="text-[11px] tracking-[0.2em] uppercase text-onyx-metal mb-4">4 · Mounting</h3>
            <div className="flex flex-wrap gap-3">
              {cfg.mounts.map((m) => (
                <button key={m.id} data-testid={`config-mount-${m.id}`} onClick={() => setMount(m.id)} className={`px-5 py-3 border text-sm transition-colors ${mount === m.id ? sel : unsel}`}>{m.label}</button>
              ))}
            </div>
          </div>

          {/* options */}
          <div>
            <h3 className="text-[11px] tracking-[0.2em] uppercase text-onyx-metal mb-4">5 · Options</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {cfg.options.map((o) => (
                <button key={o.id} data-testid={`config-option-${o.id}`} onClick={() => toggleOpt(o.id)} className={`px-4 py-4 border text-sm text-left transition-colors ${opts.includes(o.id) ? sel : unsel}`}>
                  <span className="flex items-center justify-between"><span>{o.label}</span>{opts.includes(o.id) && <Check size={15} />}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* estimate panel */}
        <div>
          <div className="sticky top-24 border border-onyx-black bg-onyx-black text-white p-8">
            <span className="text-[11px] tracking-[0.2em] uppercase text-white/50">Estimated Budget</span>
            <div className="mt-4">
              <motion.div key={est?.total} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }} className="font-display font-extrabold text-4xl tracking-tightest" data-testid="config-estimate">
                {money(est.low)} <span className="text-white/40 text-2xl">–</span> {money(est.high)}
              </motion.div>
              <p className="text-white/50 text-sm mt-2">≈ {money(est.perFt)} / linear ft · {len} ft</p>
            </div>
            <div className="h-px bg-white/10 my-6" />
            {done ? (
              <div className="text-center py-6" data-testid="config-success">
                <div className="w-12 h-12 rounded-full bg-white text-onyx-black flex items-center justify-center mx-auto mb-4"><Check size={22} /></div>
                <p className="font-display font-bold text-lg">Request sent.</p>
                <p className="text-white/50 text-sm mt-2">Our team will follow up with a formal proposal.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <p className="text-white/60 text-sm mb-2">Get this as a formal proposal:</p>
                <input data-testid="config-name" placeholder="Name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-white/50 transition-colors" />
                <input data-testid="config-email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-white/50 transition-colors" />
                <input data-testid="config-phone" placeholder="Phone (optional)" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-white/50 transition-colors" />
                <button data-testid="config-submit" disabled={submitting} className="w-full bg-white text-onyx-black py-3.5 text-[12px] tracking-[0.15em] uppercase hover:bg-onyx-wood transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  {submitting ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Request Proposal
                </button>
              </form>
            )}
            <p className="text-white/30 text-[11px] mt-4 leading-relaxed">Estimate is a budgetary range for planning. Final pricing is confirmed after site review.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
